// components/Timesheet/TimesheetView.tsx
import React from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { Add, Delete, Send } from "@mui/icons-material";
import { useTimesheet } from "../../contexts/TimesheetContext";
import { Link } from "react-router-dom";

export const TimesheetView: React.FC = () => {
  const { currentTimesheet, deleteEntry, submitTimesheet } = useTimesheet();

  const handleDeleteEntry = (entryId: string) => {
    if (window.confirm("Are you sure you want to delete this time entry?")) {
      deleteEntry(currentTimesheet.id, entryId);
    }
  };

  const handleSubmitTimesheet = () => {
    if (!currentTimesheet) return;

    if (currentTimesheet.entries.length === 0) {
      alert("Please add at least one time entry before submitting");
      return;
    }

    if (
      window.confirm(
        "Are you sure you want to submit this timesheet? You will not be able to make changes after submission."
      )
    ) {
      submitTimesheet(currentTimesheet);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (!currentTimesheet) {
    return (
      <div className="p-6">
        <Typography variant="h4" gutterBottom>
          My Timesheet
        </Typography>
        <Typography>Loading timesheet...</Typography>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <Box className="flex justify-between items-center mb-6">
        <Box>
          <Typography variant="h4" gutterBottom>
            My Timesheet
          </Typography>
          <Typography variant="h6" color="textSecondary">
            Week of {formatDate(currentTimesheet.weekStart)} to{" "}
            {formatDate(currentTimesheet.weekEnd)}
          </Typography>
        </Box>
        <Box className="space-x-2">
          <Chip
            label={currentTimesheet.status.toUpperCase()}
            color={
              currentTimesheet.status === "submitted"
                ? "success"
                : currentTimesheet.status === "approved"
                ? "primary"
                : currentTimesheet.status === "rejected"
                ? "error"
                : "default"
            }
          />
          <Button
            variant="outlined"
            startIcon={<Add />}
            component={Link}
            to="/dashboard/timesheets/entry"
          >
            Add Entry
          </Button>
          {currentTimesheet.status === "draft" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Send />}
              onClick={handleSubmitTimesheet}
              disabled={currentTimesheet.entries.length === 0}
            >
              Submit Timesheet
            </Button>
          )}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="text-center">
            <Typography color="textSecondary" gutterBottom>
              Total Hours
            </Typography>
            <Typography variant="h4" color="primary">
              {currentTimesheet.totalHours.toFixed(2)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="text-center">
            <Typography color="textSecondary" gutterBottom>
              Entries
            </Typography>
            <Typography variant="h4">
              {currentTimesheet.entries.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="text-center">
            <Typography color="textSecondary" gutterBottom>
              Average Hours/Day
            </Typography>
            <Typography variant="h4">
              {currentTimesheet.entries.length > 0
                ? (
                    currentTimesheet.totalHours /
                    currentTimesheet.entries.length
                  ).toFixed(2)
                : "0.00"}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="text-center">
            <Typography color="textSecondary" gutterBottom>
              Status
            </Typography>
            <Typography
              variant="h6"
              className={
                currentTimesheet.status === "approved"
                  ? "text-green-600"
                  : currentTimesheet.status === "rejected"
                  ? "text-red-600"
                  : currentTimesheet.status === "submitted"
                  ? "text-orange-600"
                  : "text-gray-600"
              }
            >
              {currentTimesheet.status.charAt(0).toUpperCase() +
                currentTimesheet.status.slice(1)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Timesheet Entries Table */}
      {currentTimesheet.entries.length === 0 ? (
        <Paper className="p-8 text-center">
          <Typography variant="h6" gutterBottom color="textSecondary">
            No time entries yet
          </Typography>
          <Typography variant="body1" gutterBottom color="textSecondary">
            Start by adding your first time entry for the week.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            component={Link}
            to="/dashboard/timesheets/entry"
            className="mt-4"
          >
            Add First Entry
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Break (min)</TableCell>
                <TableCell>Total Hours</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentTimesheet.entries.map((entry: any) => (
                <TableRow key={entry.id}>
                  <TableCell>{formatDate(entry.date)}</TableCell>
                  <TableCell>{entry.startTime}</TableCell>
                  <TableCell>{entry.endTime}</TableCell>
                  <TableCell>{entry.breakMinutes}</TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">
                      {entry.totalHours.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.status.toUpperCase()}
                      size="small"
                      color={
                        entry.status === "approved"
                          ? "success"
                          : entry.status === "rejected"
                          ? "error"
                          : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {currentTimesheet.status === "draft" && (
                      <Box className="space-x-1">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleDeleteEntry(entry.id)}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Submission Status Message */}
      {currentTimesheet.status !== "draft" && (
        <Paper className="p-4 mt-6 bg-blue-50 border border-blue-200">
          <Typography variant="body1">
            {currentTimesheet.status === "submitted" &&
              "Your timesheet has been submitted and is awaiting approval."}
            {currentTimesheet.status === "approved" &&
              "Your timesheet has been approved and will be processed for payroll."}
            {currentTimesheet.status === "rejected" &&
              "Your timesheet was rejected. Please review the comments and resubmit."}
          </Typography>
        </Paper>
      )}
    </div>
  );
};
